/**
 * Named app looks — decision 0055.
 *
 * A template is the generator's answer to *"show this vault as Obsidian"*: a
 * WHOLE presentation — workspace chrome, pane topology, component vocabulary,
 * the inner content layout — emitted as one ordinary versioned layout spec
 * over ordinary objects. Not a taste (a taste *"cannot honestly require a file
 * tree"*) and not a shell the runtime wraps around a generic lens (*"a
 * hard-coded `ObsidianShell` wrapped around a generic Morph table would make
 * the chrome invisible to generation, persistence, inspection, revision, and
 * taste"*). The tree below is what the pool learns, what the hub keeps, and
 * what a person can inspect and edit like any other spec.
 *
 * The look was written from a viewed reference, not from memory of the app:
 * `docs/references/canon-looks.md` (the obsidian.md render, component by
 * component) and `docs/references/targets/obsidian-note.html` (the target this
 * tree has to reach on screen). Every region and module here answers to a
 * component in that inventory — ribbon, explorer tree, tab strip, editor with
 * properties and links, backlinks pane, status bar.
 *
 * REFUSES IN WORDS rather than degrading — 0055 control 1. A template whose
 * required modules the host has not registered would arrive on screen as
 * three "not yet implemented" notes around a Morph table wearing dark colours,
 * which is exactly the substitution 0055 control 6 rejects. So the request
 * says which modules exist, and a missing one is a refusal before any spec.
 */
import {
  CAPABILITIES, ROLE_KIND, SPEC_VERSION, VOCABULARY_VERSION, APP_TEMPLATE_VERSIONS,
  DERIVATIVE_OF, isRelationRole,
  type AppTemplate, type Role, type TLayoutSpec, type TNode, type TBinding,
} from '@morph/spec';
import { TEMPLATE_VIEWS } from './types.js';
import type { GenerationRequest, TemplateView } from './types.js';

/** Re-exported from the vocabulary, where the runtime reads the same table:
 *  the version a spec is stamped with is the version the renderer draws. */
export { APP_TEMPLATE_VERSIONS };

/** The runtime modules each template cannot be drawn without. */
export const APP_TEMPLATE_MODULES: Readonly<Record<AppTemplate, readonly string[]>> = {
  // `force-graph` too: the local graph is a canonical Obsidian component
  // (the reference inventory names it; @Codex's third verdict called it the
  // largest missing one), drawn by the registered graph module in a pane.
  // `workspace-search` too: the Search tool's query box and count are a
  // registered module, not a renderer branch (@Codex, P1 on 93d57d7).
  // The window's controls lead, as they do in APP_TEMPLATE_SHAPES below and in
  // the emitted tree: one order, so a fixture built from this list can satisfy
  // the shape's first children and still exercise the tree mismatch it is for.
  'app.obsidian': ['workspace-window', 'workspace-ribbon', 'workspace-tabs', 'workspace-status', 'workspace-search', 'force-graph'],
  // Notion's shell: the nav sidebar, the topbar with the page tab, the page
  // head, the view switcher with its New button. The board is the runtime's
  // own board presentation wearing the template's tokens.
  // `calendar` and `timeline` too: the page's views beyond the board and
  // the table are those two registered modules inside the same shell.
  'app.notion': ['workspace-nav', 'workspace-topbar', 'workspace-pagehead', 'view-switcher', 'workspace-search', 'calendar', 'timeline'],
  // Coda's doc: the page-tree sidebar, the topbar, the page head with its
  // cover, buttons as blocks over the table.
  'app.coda': ['workspace-nav', 'workspace-topbar', 'workspace-pagehead', 'doc-actions'],
  // Word's window: the title bar, the ribbon, the document-flow module as
  // the sheet on the desk, the status bar.
  'app.word': ['workspace-topbar', 'doc-ribbon', 'document-flow', 'workspace-status'],
  // Fibery's workspace: the sidebar with the spaces, the topbar, the view
  // bar, the view switcher with New, the view's search. The board and the
  // table are the runtime's own presentations wearing the template's tokens.
  'app.fibery': ['workspace-nav', 'workspace-topbar', 'workspace-pagehead', 'view-switcher', 'workspace-search'],
  // Fabric's window: the title bar and the assistant panel's composer —
  // the search module, answering from the library's retrieval.
  'app.fabric': ['workspace-topbar', 'workspace-search'],
};

/** The refusal, as its own class so `compile` can report it in words rather
 *  than as `generator-threw` — the same shape `AcceptedNoLongerLegal` takes. */
export class TemplateUnsatisfiable extends Error {
  constructor(
    readonly template: AppTemplate,
    readonly problems: readonly string[],
  ) {
    super(`${template} cannot be produced: ${problems.join('; ')}`);
    this.name = 'TemplateUnsatisfiable';
  }
}

/**
 * Does this spec SATISFY the template that was asked for? Checked by
 * `compile` on every generator's result — @Codex, P2 on d0c42da: a
 * model-backed generator that ignored `req.template` and returned a valid
 * generic spec passed validation, and the host then hid Morph's chrome
 * around a lens with no ribbon. The template's own builder is one producer
 * of such a spec; this is the contract every producer is held to: the
 * provenance stamp, its version, and every required module present as a
 * capability node.
 */
export function templateShortfalls(spec: TLayoutSpec, template: AppTemplate): string[] {
  const out: string[] = [];
  const stamp = (spec as { template?: { id?: string; version?: string } }).template;
  if (stamp?.id !== template) out.push(`the spec is not stamped as ${template}`);
  else if (stamp.version !== APP_TEMPLATE_VERSIONS[template]) {
    out.push(`the spec carries ${template} ${stamp.version}, not ${APP_TEMPLATE_VERSIONS[template]}`);
  }
  // The TOPOLOGY — the tree's SHAPE, not a set of names. @Codex, P2 on
  // b0af641 and P1 on 4ab498b: the first versions collected modules and
  // purposes into sets, so a generic collection with three regions labelled
  // explorer, editor and backlinks scattered anywhere, and the chrome nodes
  // anywhere else, satisfied them — and the Obsidian grid depends on the
  // panes and chrome occupying their prescribed positions. 0055 calls the
  // topology an exact hard constraint; `matchShape` holds the tree to it,
  // node by node, each required module IN ITS PLACE (a module set on its
  // own also refused the honest case: a vault with no relation role emits
  // no graph pane, and the shape says so with `optional`).
  const target = (spec as { target?: { cardinality?: string } }).target;
  out.push(...matchShape(spec.root, APP_TEMPLATE_SHAPES[template], 'the workspace', target?.cardinality));
  return out;
}

/**
 * The SHAPE a template's tree must have — the app-defining topology of 0055,
 * written as the tree itself. `children` is matched in ORDER and in full;
 * a shape marked `optional` may be absent (the graph pane, for a shape with
 * no relation role); a shape without `children` constrains the node and
 * leaves what is inside it to taste (the editor's fields are the person's).
 */
export interface TemplateShape {
  readonly kind?: 'region' | 'capability' | 'collection' | 'field' | 'media' | 'node-slot';
  readonly name: string;
  /** One of several shapes may stand here — a page's VIEW is a board or a
   *  table (a collection) or a calendar or a timeline (a module). */
  readonly anyOf?: readonly TemplateShape[];
  readonly purpose?: string;
  readonly capability?: string;
  readonly source?: string;
  readonly children?: readonly TemplateShape[];
  readonly optional?: boolean;
  /** This entry may stand MORE THAN ONCE, consuming consecutive kids that fit
   *  it. With `optional`, zero or more; without, one or more. Added for the
   *  object page's attachments: an object may carry a still and a recording
   *  and a document, and a singular entry turned the second into "more
   *  children than the template allows". */
  readonly many?: boolean;
  /**
   * GATE an alternative on the spec's own target cardinality — decision
   * 0057/task 0176, @Codex: *"validation should gate the alternative using
   * the spec's preserved cardinality … Do not merely add an ungated `anyOf`;
   * that would allow a large-set request to emit an object page or a
   * single-object request to emit another singleton board and still satisfy
   * the template."*
   *
   * `'single'` stands only when the spec targets one object; `'set'` only
   * when it targets more than one. A shape with no `when` stands either way,
   * which is every shape written before this existed.
   *
   * The gate is only as good as the discriminator reaching it: a builder that
   * coerces `single` to `small-set` before stamping `target` has thrown the
   * discriminator away, and this field then reads a constant. That coercion
   * is why the ruling names it.
   */
  readonly when?: 'single' | 'set';
  /**
   * THE ROLE THIS POSITION MUST BIND, and whether a person can change it —
   * task `0184`, from @Codex's P2 on `660c7bd`.
   *
   * A position declared only as `field` is a position the template cannot
   * refuse on. Their case: the Coda row detail exists so somebody can open a
   * row and change its title, and a generator could bind `body` there, or bind
   * the title read-only, and still satisfy both `validateSpec()` and
   * `templateShortfalls()`. A contract that admits a read-only body where its
   * whole purpose is an editable title does not describe itself.
   *
   * Only for a position whose ROLE is the point. Most fields are the person's
   * and a template that named them would be deciding taste — which is what the
   * note above means by *the editor's fields are the person's*.
   */
  readonly role?: string;
  readonly direction?: 'read' | 'read-write';
}

export const APP_TEMPLATE_SHAPES: Readonly<Record<AppTemplate, TemplateShape>> = {
  'app.obsidian': {
    kind: 'region', name: 'the workspace row', children: [
      { kind: 'capability', name: 'the window controls', capability: 'workspace-window' },
      { kind: 'capability', name: 'the ribbon', capability: 'workspace-ribbon' },
      { kind: 'region', name: 'the left sidebar', purpose: 'sidebar', children: [
        { kind: 'region', name: 'the explorer pane', purpose: 'explorer', children: [
          { kind: 'collection', name: 'the file list', source: 'siblings' },
        ] },
        { kind: 'region', name: 'the search pane', purpose: 'search', children: [
          { kind: 'capability', name: 'the search box', capability: 'workspace-search' },
          { kind: 'collection', name: 'the search results' },
        ] },
      ] },
      { kind: 'region', name: 'the main column', children: [
        { kind: 'capability', name: 'the tab strip', capability: 'workspace-tabs' },
        { kind: 'region', name: 'the editor pane', purpose: 'editor' },
        { kind: 'region', name: 'the graph view', purpose: 'graph', optional: true, children: [
          { kind: 'capability', name: 'the vault graph', capability: 'force-graph' },
        ] },
      ] },
      { kind: 'region', name: 'the sidebar', purpose: 'sidebar', children: [
        { kind: 'region', name: 'the backlinks pane', purpose: 'backlinks', children: [
          { kind: 'collection', name: 'the backlinks list' },
        ] },
        { kind: 'region', name: 'the graph pane', purpose: 'graph', optional: true, children: [
          { kind: 'capability', name: 'the local graph', capability: 'force-graph' },
        ] },
      ] },
      { kind: 'capability', name: 'the status bar', capability: 'workspace-status' },
    ],
  },
  'app.notion': {
    kind: 'region', name: 'the workspace row', children: [
      { kind: 'capability', name: 'the nav sidebar', capability: 'workspace-nav' },
      { kind: 'region', name: 'the page column', children: [
        { kind: 'capability', name: 'the topbar', capability: 'workspace-topbar' },
        // TWO legal arrangements AT THE PAGE POSITION, gated on the spec's
        // preserved cardinality — 0176's ruling. The same stable Notion page
        // shell showing a different semantic subject, not a second root shape.
        { name: 'the page', anyOf: [
          { when: 'set', kind: 'region', name: 'the page over a set', purpose: 'editor', children: [
            { kind: 'capability', name: 'the page head', capability: 'workspace-pagehead' },
            { kind: 'capability', name: 'the view switcher', capability: 'view-switcher' },
            { kind: 'capability', name: 'the search', capability: 'workspace-search' },
            { name: 'the view', anyOf: [
              { kind: 'collection', name: 'the board or the table' },
              { kind: 'capability', name: 'the calendar', capability: 'calendar' },
              { kind: 'capability', name: 'the timeline', capability: 'timeline' },
            ] },
          ] },
          // ONE OBJECT: head, then the object's own title, properties and
          // body. No view switcher and no collection — the ruling's *"the
          // collection's `nt-card` is absent at single cardinality; therefore
          // no inert button-shaped remnant remains."*
          { when: 'single', kind: 'region', name: 'the page over one object', purpose: 'editor', children: [
            { kind: 'capability', name: 'the page head', capability: 'workspace-pagehead' },
            { kind: 'field', name: "the object's title" },
            // An attachment is not a property and is not dropped either
            // (@Codex, 27a1a65). Optional: most shapes carry none.
            { kind: 'media', name: 'its attachments', optional: true, many: true },
            { kind: 'region', name: 'the properties', purpose: 'properties', optional: true },
            { kind: 'region', name: 'the relations', purpose: 'relations', optional: true },
            { kind: 'field', name: 'the body', optional: true },
          ] },
        ] },
      ] },
    ],
  },
  'app.word': {
    kind: 'region', name: 'the window', children: [
      { kind: 'capability', name: 'the title bar', capability: 'workspace-topbar' },
      { kind: 'capability', name: 'the ribbon', capability: 'doc-ribbon' },
      { kind: 'region', name: 'the desk', purpose: 'editor', children: [
        { kind: 'capability', name: 'the document', capability: 'document-flow' },
      ] },
      { kind: 'capability', name: 'the status bar', capability: 'workspace-status' },
    ],
  },
  'app.fabric': {
    kind: 'region', name: 'the window', children: [
      { kind: 'region', name: 'the note column', children: [
        { kind: 'capability', name: 'the title bar', capability: 'workspace-topbar' },
        { kind: 'region', name: 'the note', purpose: 'editor' },
      ] },
      { kind: 'region', name: 'the assistant panel', purpose: 'search', children: [
        { kind: 'capability', name: 'the composer', capability: 'workspace-search' },
        { kind: 'collection', name: 'what it found' },
      ] },
    ],
  },
  'app.fibery': {
    kind: 'region', name: 'the workspace row', children: [
      { kind: 'capability', name: 'the sidebar', capability: 'workspace-nav' },
      { kind: 'region', name: 'the view column', children: [
        { kind: 'capability', name: 'the topbar', capability: 'workspace-topbar' },
        // The same two gated arrangements Notion has (0176) — @Codex: *"The
        // same ruling applies to Fibery: keep the board card as its open
        // action, then emit a real object page after navigation rather than
        // reusing the singleton card."*
        { name: 'the view', anyOf: [
          { when: 'set', kind: 'region', name: 'the view over a set', purpose: 'editor', children: [
            { kind: 'capability', name: 'the view bar', capability: 'workspace-pagehead' },
            { kind: 'capability', name: 'the view switcher', capability: 'view-switcher' },
            { kind: 'capability', name: 'the search', capability: 'workspace-search' },
            { kind: 'collection', name: 'the board or the table' },
          ] },
          { when: 'single', kind: 'region', name: 'the view over one object', purpose: 'editor', children: [
            { kind: 'capability', name: 'the view bar', capability: 'workspace-pagehead' },
            { kind: 'field', name: "the object's title" },
            // An attachment is not a property and is not dropped either
            // (@Codex, 27a1a65). Optional: most shapes carry none.
            { kind: 'media', name: 'its attachments', optional: true, many: true },
            { kind: 'region', name: 'the properties', purpose: 'properties', optional: true },
            { kind: 'region', name: 'the relations', purpose: 'relations', optional: true },
            { kind: 'field', name: 'the body', optional: true },
          ] },
        ] },
      ] },
    ],
  },
  'app.coda': {
    kind: 'region', name: 'the workspace row', children: [
      { kind: 'capability', name: 'the page tree', capability: 'workspace-nav' },
      { kind: 'region', name: 'the doc column', children: [
        { kind: 'capability', name: 'the topbar', capability: 'workspace-topbar' },
        // The third of the three gated pairs (0176). Coda's own gesture is a
        // row EXPAND: a table row opens the row's own detail, which is that
        // row's properties laid out down a page — not the table again with
        // one row in it. The buttons belong to the table's page and are
        // absent from the row's, which is 0057's *"no section the pool
        // cannot back"* read at the other cardinality.
        { name: 'the doc', anyOf: [
          { when: 'set', kind: 'region', name: 'the doc over a set', purpose: 'editor', children: [
            { kind: 'capability', name: 'the page head', capability: 'workspace-pagehead' },
            { kind: 'capability', name: 'the buttons', capability: 'doc-actions' },
            { kind: 'collection', name: 'the table' },
          ] },
          { when: 'single', kind: 'region', name: 'the doc over one row', purpose: 'editor', children: [
            { kind: 'capability', name: 'the page head', capability: 'workspace-pagehead' },
            /* THE POSITION'S PURPOSE, DECLARED — task `0184`. This read
               `{ kind: 'field', name: "the row's title" }`, which admits any
               field at all: the row detail exists so a person can change the
               title, and nothing said so where a check could read it. */
            { kind: 'field', name: "the row's title", role: 'title', direction: 'read-write' },
            // An attachment is not a property and is not dropped either
            // (@Codex, 27a1a65). Optional: most shapes carry none.
            { kind: 'media', name: 'its attachments', optional: true, many: true },
            { kind: 'region', name: 'the properties', purpose: 'properties', optional: true },
            { kind: 'region', name: 'the relations', purpose: 'relations', optional: true },
            { kind: 'field', name: 'the body', optional: true },
          ] },
        ] },
      ] },
    ],
  },
};

/**
 * Hold one node to one shape, in words that name the place.
 *
 * `cardinality` is the spec's own `target.cardinality`, threaded so a shape
 * may be GATED on it (`when`). It is optional so a caller holding a subtree to
 * a shape that gates nothing needs no ceremony — but `templateShortfalls`
 * always passes it, so every production check is gated.
 */
export function matchShape(node: TNode | undefined, shape: TemplateShape, at: string,
  cardinality?: string): string[] {
  const n = node as { kind?: string; purpose?: string; capability?: string; source?: string; children?: TNode[] } | undefined;
  const what = (x: typeof n): string => x === undefined ? 'nothing'
    : x.kind === 'region' ? `a region${x.purpose ? ` (${x.purpose})` : ''}`
    : x.kind === 'capability' ? `the ${x.capability} module` : `a ${x.kind}`;
  // ONE OF several: the first alternative that fits stands; none fitting
  // is reported once, naming the place and every alternative.
  if (shape.anyOf !== undefined) {
    // A gated list checked WITHOUT a cardinality cannot be checked at all, and
    // the permissive reading of that — open every alternative — IS the ungated
    // `anyOf` the ruling forbids, arriving through a caller that forgot an
    // argument rather than through the shape. So it refuses loudly.
    if (cardinality === undefined && shape.anyOf.some((alt) => alt.when !== undefined)) {
      return [`${at}: ${shape.name} is gated on cardinality and none was supplied to matchShape`];
    }
    // Only the alternatives this spec's cardinality ADMITS are considered. An
    // ungated list would let a large-set request satisfy the template with an
    // object page, which is the exact failure 0176's ruling names.
    const open = shape.anyOf.filter((alt) => alt.when === undefined
      || (alt.when === 'single' ? cardinality === 'single' : cardinality !== 'single'));
    for (const alt of open) if (matchShape(node, alt, at, cardinality).length === 0) return [];
    // ONE alternative open means there is nothing to be ambiguous about, so
    // report THAT shape's own problems rather than "should be one of: it".
    // The gate makes this the common case and it is strictly the better
    // message: a page missing its view now says which view, where, instead of
    // naming the arrangement it failed to be.
    if (open.length === 1) return matchShape(node, open[0]!, at, cardinality);
    const named = open.length ? open : shape.anyOf;
    const gate = open.length === shape.anyOf.length ? '' : ` at cardinality ${cardinality}`;
    return [`${at} should be ${shape.name}${gate} (${named.map((a) => a.name).join(', or ')}), found ${what(n)}`];
  }
  if (n === undefined || n.kind !== shape.kind
      || (shape.purpose !== undefined && n.purpose !== shape.purpose)
      || (shape.capability !== undefined && n.capability !== shape.capability)
      || (shape.source !== undefined && n.source !== shape.source)) {
    return [`${at} should be ${shape.name}, found ${what(n)}`];
  }
  /* AND THE BINDING, where the shape names one — task `0184`. Reported
     SEPARATELY from the shape mismatch above and naming what was found,
     because a generator author reading "should be the row's title, found a
     field" learns nothing: the node IS a field, and the wrong thing about it
     is which role it reached for. */
  if (shape.role !== undefined || shape.direction !== undefined) {
    const b = (node as { binding?: { role?: string; direction?: string } } | undefined)?.binding;
    const wrong: string[] = [];
    if (shape.role !== undefined && b?.role !== shape.role) {
      wrong.push(`bound to ${b?.role === undefined ? 'nothing' : b.role} rather than ${shape.role}`);
    }
    if (shape.direction !== undefined && b?.direction !== shape.direction) {
      wrong.push(`${b?.direction === undefined ? 'unbound' : b.direction} where the template requires ${shape.direction}`);
    }
    if (wrong.length) return [`${at}: ${shape.name} is ${wrong.join(', and ')}`];
  }
  if (shape.children === undefined) return [];
  const out: string[] = [];
  const kids = n.children ?? [];
  let k = 0;
  for (const want of shape.children) {
    // A REPEATABLE entry consumes as many consecutive kids as fit it, and
    // zero is allowed — task 0176, @Codex on the object page's attachments.
    // An object may carry a still and a recording and a document; a singular
    // entry made the second one "more children than the template allows",
    // which is a template limit standing in for a design decision nobody
    // made. `optional` says "none is fine"; `many` says "and more than one
    // is too", and they are independent: a required repeatable entry would
    // demand at least one.
    if (want.many) {
      if (!want.optional && matchShape(kids[k], want, `${shape.name}'s child ${k + 1}`, cardinality).length) {
        out.push(...matchShape(kids[k], want, `${shape.name}'s child ${k + 1}`, cardinality));
      }
      while (k < kids.length
        && matchShape(kids[k], want, `${shape.name}'s child ${k + 1}`, cardinality).length === 0) k += 1;
      continue;
    }
    const problems = matchShape(kids[k], want, `${shape.name}'s child ${k + 1}`, cardinality);
    // An optional shape that does not fit is simply absent; the next
    // required one is held to the same position.
    if (problems.length && want.optional) continue;
    out.push(...problems);
    k += 1;
  }
  if (k < kids.length) {
    out.push(`${shape.name} has ${kids.length - k} more child(ren) than the template allows, starting with ${what(kids[k] as typeof n)}`);
  }
  return out;
}


/** How a binding is minted — the baseline's own `bindingFor`, passed in so this
 *  file does not have to know the chooseable-enum rule it encodes. */
export type Bind = (slot: string, role: Role, direction?: 'read') => TBinding;

/**
 * The whole presentation for the named template, or a refusal.
 *
 * `ranked` is the baseline's role ranking with the person's suppressed roles
 * already removed — taste still decides which properties the editor shows
 * and in what order, which is exactly the degree of freedom the template
 * declares variable. What it does NOT vary is the topology.
 */
export function buildAppTemplate(
  req: GenerationRequest & { readonly template: AppTemplate },
  ranked: readonly Role[],
  bind: Bind,
): TLayoutSpec {
  const problems: string[] = [];
  // The inventory is REQUIRED with a template, not optional — @Codex, P2 on
  // ebf7b8c: treating its absence as proof that every module exists deferred
  // the refusal to a visible runtime note around a partial app, which is
  // the substitution the contract forbids.
  if (req.capabilities === undefined) {
    problems.push(`a request for ${req.template} must say which runtime modules are registered`);
  }
  const roles = new Set(req.shape.roles.map((r) => r.role));
  const declared = new Set([...roles, ...(req.declaredRoles ?? [])]);
  const named = req.template === 'app.notion' ? 'a Notion page names every card'
    : req.template === 'app.coda' ? 'a Coda table names every row'
    : req.template === 'app.word' ? 'a Word document names every section'
    : req.template === 'app.fibery' ? 'a Fibery database names every entity'
    : req.template === 'app.fabric' ? 'a Fabric library names every item' : 'an Obsidian vault names every note';
  if (!declared.has('title')) {
    problems.push(`${named} by its title, and this shape has none`);
  }
  // A Notion page's board is grouped by a STATUS: the reference's four
  // tinted columns are the status options. A shape with no enum to group
  // by has no board, and the template says so rather than drawing a list.
  //
  // AT A SET ONLY — @Codex's P2 on `0d83f75`. The requirement is about the
  // BOARD, and 0176's single-cardinality branch builds no board, no grouping
  // and no view switcher: it is a title, a properties region and a relations
  // region. Asked unconditionally it threw before `buildFor()` ran, so a
  // perfectly valid object page — a shape with a title and a body and no
  // enum anywhere — could not use either detail presentation, refused for
  // lacking a column set it was never going to draw.
  //
  // The two are one condition written twice because the two messages name
  // different things (a status, a state); the gate is the same.
  const grouped = req.shape.cardinality !== 'single';
  if (grouped && req.template === 'app.notion' && !req.shape.roles.some((r) => r.kind === 'enum')) {
    problems.push('a Notion board groups its cards by a status, and this shape has no enum to group by');
  }
  // Fibery's board is the same: its columns are the entity's STATE options.
  if (grouped && req.template === 'app.fibery' && !req.shape.roles.some((r) => r.kind === 'enum')) {
    problems.push('a Fibery board groups its cards by a state, and this shape has no enum to group by');
  }
  // A suppressed title is a contradiction, not an override — the template
  // needs one and a person's hide is honoured absolutely (@Codex, P2).
  if (req.taste?.suppressedRoles?.includes('title')) {
    problems.push(`the title is hidden, and ${named.replace(' names every', ' cannot name its')}s without one`);
  }
  if (problems.length) throw new TemplateUnsatisfiable(req.template, problems);

  // Only one template today, so the switch is a statement of where the next
  // one goes rather than a dispatch anybody needs yet.
  const buildFor = (r: GenerationRequest & { readonly template: AppTemplate }): TLayoutSpec => {
    switch (r.template) {
      case 'app.obsidian':
        return obsidian(r, ranked, bind);
      case 'app.notion':
        return notion(r, ranked, bind);
      case 'app.coda':
        return coda(r, ranked, bind);
      case 'app.word':
        return word(r, bind);
      case 'app.fibery':
        return fibery(r, ranked, bind);
      case 'app.fabric':
        return fabric(r, ranked, bind);
    }
  };
  const spec = buildFor(req);

  /**
   * THE MODULES THIS TREE ACTUALLY NEEDS — @Codex's P2 on `ed4f7a7`.
   *
   * `APP_TEMPLATE_MODULES` is the set a template's SET view is drawn from,
   * and it was checked unconditionally. Coda's single-object branch
   * deliberately emits no `doc-actions` — the buttons are a fact about a
   * doc's table, not about one row's detail page — so a runtime that had
   * registered every module the row page draws was still refused, for the
   * absence of a component the page never asks for. A refusal naming a
   * module the tree does not contain is a refusal about nothing.
   *
   * DERIVED from the emitted tree rather than tabulated per cardinality,
   * which is the difference between this and a second table to keep in
   * step: a branch that stops emitting a module stops requiring it, and a
   * seventh app requires exactly what it draws with no entry to add. The
   * declared table stays as the SUPERSET a spec is held to by
   * `templateShortfalls`; this is the subset one tree reached for.
   *
   * Still a refusal before the spec is handed out — the build is pure and
   * the spec is dropped on the throw — so `0055` control 1 holds.
   */
  /**
   * AND EVERY VIEW THE SWITCHER CAN REACH — @Codex's P2 on `552d25e`.
   *
   * The subset above is what THIS tree draws, and a page with a view switcher
   * draws exactly one view at a time. So a runtime with no `calendar` module
   * passed the check for a Notion board — and then the emitted switcher
   * offered Calendar anyway, because the host populates that row from the
   * template's own view list rather than from the tree. Activating it
   * regenerates, and the person's page is replaced by a missing-module
   * refusal: the substitution `0055` control 1 exists to prevent, arriving
   * one gesture after the check said yes.
   *
   * DERIVED, like the subset it extends. The alternates are BUILT — the same
   * builder, the same request with a different `templateView` — and their
   * capabilities unioned in, so a fifth view added to `TEMPLATE_VIEWS` and
   * handled by a builder is covered with no table to keep in step. A builder
   * that maps an unknown view onto one it already draws (Fibery sends
   * calendar and timeline to its board) contributes nothing new, which is
   * correct: its switcher offers only what it can draw.
   *
   * Gated on the tree CONTAINING a switcher, which is what preserves the
   * exemption the subset was written for: Coda's single-object page emits no
   * switcher and no `doc-actions`, so it still requires neither. A page that
   * cannot switch cannot advertise a view it cannot draw.
   *
   * The builders are pure and every alternate spec is dropped here, so this
   * costs a few tree constructions and hands nothing out.
   */
  const treeIds = new Set(capabilityIds(spec.root));
  const reachable = new Set(treeIds);
  if (treeIds.has('view-switcher')) {
    for (const view of TEMPLATE_VIEWS) {
      if (view === req.templateView) continue;
      for (const id of capabilityIds(buildFor({ ...req, templateView: view }).root)) reachable.add(id);
    }
  }
  const registered = new Set(req.capabilities ?? []);
  const missing = [...reachable].filter((id) => !registered.has(id));
  if (missing.length) {
    throw new TemplateUnsatisfiable(req.template,
      missing.map((id) => `the ${id} module is not registered in this runtime`));
  }
  return spec;
}

/** Every capability this tree asks the runtime for, in tree order. */
function capabilityIds(node: TNode): string[] {
  const self = (node as { kind?: string; capability?: string });
  const kids = (node as { children?: TNode[]; items?: { spec: TNode }[] });
  return [
    ...(self.kind === 'capability' && self.capability !== undefined ? [self.capability] : []),
    ...(kids.children ?? []).flatMap(capabilityIds),
    ...(kids.items ?? []).map((i) => i.spec).flatMap(capabilityIds),
  ];
}

/**
 * `app.fabric` — a note in a floating window with the assistant panel
 * (decision 0055's sixth look, task 0170). Read from the pinned reference
 * (`canon-looks.md`, the fabric.so hero at full resolution): a white window
 * with a large radius over a blurred photo; a thin title bar naming the
 * note in muted grey with the search, the people and ··· at its right; the
 * note — a large light-grey title, then its media, then its prose in a
 * 16px sans; and at the right the ASSISTANT PANEL: a header, a model pill,
 * a thread, and a composer at the foot. The composer here is the library's
 * own retrieval — Fabric's "search by meaning" — and the thread is what it
 * found: a card per hit, opening the item. Nothing else in the panel is
 * drawn as a control (0056).
 *
 *   title bar / note  |  composer / the hits
 *
 * A single-object look, like the vault: the door opens ON an item.
 */
function fabric(req: GenerationRequest, ranked: readonly Role[], bind: Bind): TLayoutSpec {
  const kinds = (r: Role) => ROLE_KIND[r];
  /**
   * A PERSON'S HIDE REACHES THIS PAGE TOO — task `0222`.
   *
   * `ranked` arrives with `taste.suppressedRoles` already removed, which is
   * the contract `buildAppTemplate` states. Fabric builds its page from
   * `req.shape.roles` instead, because its item is an archive record rather
   * than a generic object page, and that is the whole reason it never
   * reached `objectPageNodes` — whose first helper is this predicate and
   * which applies it to attachments, relations, scalars and prose alike.
   *
   * So the door that shows the SAME objects as its five neighbours honoured
   * a suppression they honour and it did not. Joaquín's amendment is the
   * governing sentence: if the data has the same source then what you do in
   * one happens in the other.
   *
   * It is applied in TWO places below, and the second is the one a reading
   * of the media filter alone would miss: the prose FALLBACK reads the shape
   * directly, so a hidden body came back through it even though both
   * `ranked` lookups above it correctly found nothing.
   */
  const suppressed = (r: Role) => req.taste?.suppressedRoles?.includes(r) === true;
  const roles = req.shape.roles.map((r) => r.role).filter((r) => !suppressed(r));
  const prose = ranked.find((r) => r === 'body') ?? ranked.find((r) => r === 'transcript')
    ?? roles.find((r) => r === 'body' || r === 'transcript');
  // The note's MEDIA, in the order a Fabric item shows them: the film, the
  // recording, the frame — whatever the shape carries.
  const media = roles.filter((r) => kinds(r) === 'av' || kinds(r) === 'media');
  const read = (slot: string, role: Role): TBinding =>
    ({ slot, role, direction: 'read' }) as TBinding;
  const mod = (id: string, capability: 'workspace-topbar' | 'workspace-search', channels?: Record<string, TBinding>): TNode => ({
    kind: 'capability', id, capability, version: CAPABILITIES[capability].version,
    ...(channels ? { channels } : {}),
  }) as unknown as TNode;
  const note: TNode = {
    kind: 'region', id: 'fa-note', purpose: 'editor', flow: 'stack',
    children: [
      { kind: 'field', id: 'fa-title', binding: bind('fa-title', 'title') },
      // MEDIA nodes, as the baseline emits them: a player for the film and
      // the recording, a still for the frame — OPTIONAL, because the library
      // is ragged (a film with no recording says nothing about one); the
      // still carries an aspect or it is a width with no height.
      ...media.map((r, i) => ({
        kind: 'media', id: `fa-media-${i}`, binding: read(`fa-media-${i}`, r), fit: 'contain', optional: true,
        ...(kinds(r) === 'media' ? { aspect: '16/9' } : {}),
      })),
      ...(prose ? [{ kind: 'field', id: 'fa-body', binding: bind('fa-body', prose) }] : []),
    ],
  } as unknown as TNode;
  const column: TNode = {
    kind: 'region', id: 'fa-column', flow: 'stack',
    children: [mod('fa-titlebar', 'workspace-topbar', { label: read('fa-tab-name', 'title') }), note],
  } as unknown as TNode;
  // The panel's thread: the hits, each a card the host routes to its item —
  // the SAME ids the vault's search pane uses, so the host answers both
  // through one door.
  const panel: TNode = {
    kind: 'region', id: 'fa-panel', purpose: 'search', flow: 'stack',
    children: [
      mod('fa-composer', 'workspace-search'),
      {
        kind: 'collection', id: 'ws-search-results', flow: 'stack',
        affordances: ['select', 'navigate'],
        items: [{
          spec: {
            kind: 'card', id: 'ws-search-hit', affordances: ['select'],
            bindings: [read('ws-search-name', 'title'), ...(prose ? [read('ws-search-text', prose)] : [])],
          },
        }],
      },
    ],
  } as unknown as TNode;
  const root = {
    kind: 'region', id: 'fa-window', flow: 'row',
    children: [column, panel],
  } as unknown as TNode;
  return {
    specVersion: SPEC_VERSION, vocabularyVersion: VOCABULARY_VERSION,
    template: { id: 'app.fabric', version: APP_TEMPLATE_VERSIONS['app.fabric'] },
    // FROM THE EMITTED TREE, like its three neighbours — see `emitsMediaNode`.
    // Fabric builds its own page rather than through `objectPageNodes`, so it
    // was never reached by the cardinality gate and read `hasMedia` bare. That
    // is the same substitution of a declaration for an emission, and it is
    // fixed here rather than named in a comment: a residual left in prose is a
    // review finding somebody pays for twice.
    target: { cardinality: 'single', mediaWeight: emitsMediaNode(root) ? 'mixed' : 'text',
      context: 'authoring' },
    density: 'balanced', typography: 'utility', palette: 'neutral',
    root,
  } as unknown as TLayoutSpec;
}

/**
 * `app.fibery` — a database as a board (decision 0055's fifth look, task
 * 0170; Fibery joined the canon under task 0150). Read from the pinned
 * references (`canon-looks.md`: the board and table crops, the homepage
 * hero's sidebar): a white sidebar with the workspace, Home, Favorites and
 * the SPACES as a tree; a compact bar naming the view with its star; the
 * toolbar — the view's search and "+ New"; then the board of state columns
 * as plain bold headers with counts, white hairline cards with a TYPE
 * BADGE, a title and field chips — or the table, a hairline grid with
 * pill states and the relations as chips.
 *
 *   sidebar | topbar / view bar / view switcher / search / board or table
 *
 * The same registered modules Notion's look uses, fed by the host; no
 * region here is anything but layout.
 */
/**
 * The OBJECT PAGE, shared by the three looks that have one — task 0176.
 *
 * Notion's page, Fibery's view and Coda's row detail were three copies of
 * these regions, which is how @Codex's review of `27a1a65` found the same
 * two defects three times over. One builder, prefixed per look, so a fix
 * lands in all three and a fourth look costs an id.
 *
 * ## Three corrections from that review
 *
 * **RELATIONS ARE CHOSEN BY ROLE, NOT BY KIND.** The copies filtered on
 * ROLE_KIND === 'ref', which admits `source` — provenance, where a value
 * came from — and `RELATION_ROLES` deliberately excludes it while
 * `mutationLegalFor` refuses `link` on it. So a page could offer Connections
 * whose only slot no gesture can write. `baseline.ts` had already been
 * corrected from kind to `isRelationRole` for exactly this, by @Codex on
 * #119, one layer out and never carried here.
 *
 * **AND THEY COME FROM THE SHAPE, NOT FROM `ranked`.** `rankRoles` drops
 * every role under `roleCoverageFloor`, which is a PRESENTATION rule: it
 * keeps a lens from being cluttered by a property two objects in a hundred
 * carry. On a detail page it becomes a REACHABILITY rule instead — a
 * relation on 10% of a pool gets no slot, a slot is the only attach
 * surface, and the relation can never become 11%. That is the sparse-pool
 * bootstrap failure `surfacedRelations` exists to prevent, surviving in a
 * new place. A detail page is WHERE relations belong, so every relation
 * role the shape declares gets one, without the threshold a card needs.
 *
 * Suppression still applies and outranks both: a coverage floor is the
 * generator's own guess, a declaration outranks a guess, and a person
 * saying they do not want to see a role outranks the declaration — which is
 * what they were suppressing.
 *
 * **AND AN ATTACHMENT IS NOT A PROPERTY.** The copies dropped media, av and
 * doc roles from the properties row — correctly, they are not scalar cells
 * — and then emitted them nowhere. A page about an object whose substantive
 * content is a photo, a recording or a document showed its title and its
 * scalars and nothing else. They get their own region now, drawn with the
 * media node the runtime already has.
 */
/**
 * WHAT THE SPEC ACTUALLY DRAWS, for the taste key — @Codex's P2 on `d881f5b`.
 *
 * Surface-scoped taste is keyed by `(context, cardinality, mediaWeight)`, and
 * the three named-app builders stamped that third term from
 * `req.shape.hasMedia && cardinality === 'single'` — the DECLARATION. The page
 * they emit can disagree with it, and a person is what makes it disagree:
 * `objectPageNodes` drops every attachment role in `taste.suppressedRoles`, so
 * HIDING the image leaves a text-only page still filing its feedback in the
 * mixed-media cell, and accepted genomes from it come back to pages that have
 * no media on them.
 *
 * That is the second time this exact stamp has been wrong in the same
 * direction. The first was `hasMedia` alone stamping the BOARD and the TABLE
 * `mixed` (@Codex on `ee1dd16`), repaired by adding the cardinality term — a
 * second condition standing in for the question nobody was asking. The
 * question is whether a media node was EMITTED, so this reads the emitted tree
 * and no third condition can be needed.
 *
 * It also answers the set branches by construction rather than by a rule about
 * them: they filter `media`, `av` and `doc` out of what they draw, so their
 * roots hold no media node and they stamp `text` exactly as the cardinality
 * gate made them. A future set surface that DOES draw a thumbnail stamps
 * `mixed`, which is the right answer and one the gate would have got wrong.
 *
 * The walk is over any nested value rather than over `children` by name: a
 * node shape that carries its descendants under another key is a shape this
 * must not silently stop seeing, and `0064`'s question — which consumer
 * assumes this set is closed — has one answer here rather than a list.
 *
 * NAMED `emitsMediaNode` AND NOT `drawsMedia`, which it was for one commit.
 * `render-html.ts` already has a `drawsMedia`, and it is a SET OF ROLES rather
 * than a predicate over a tree — a reader grepping the name would have got two
 * different things with one word. That is `0064`'s vocabulary question asked
 * of my own diff: what did this introduce, and does an existing consumer of
 * that name assume something else.
 */
function emitsMediaNode(value: unknown): boolean {
  if (Array.isArray(value)) return value.some(emitsMediaNode);
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  if (record['kind'] === 'media') return true;
  return Object.values(record).some(emitsMediaNode);
}

function objectPageNodes(
  prefix: string, req: GenerationRequest, ranked: readonly Role[], bind: Bind,
): TNode[] {
  const kinds = (r: Role) => ROLE_KIND[r];
  const suppressed = (r: Role) => req.taste?.suppressedRoles?.includes(r) === true;
  /**
   * PROSE COMES FROM THE SHAPE, not from `ranked` — @Codex's P2 on `7c286bf`,
   * and it is the FOURTH instance of one shape on this page. `ranked` drops
   * every role under `roleCoverageFloor`, a presentation rule about a lens
   * over MANY objects; relations were taught to read past it, then
   * attachments, then ordinary scalars, and prose was still reading it.
   *
   * Sparse prose then fell through TWICE. `prose` stayed undefined, so the
   * trailing body field was never emitted — and `isScalar` excludes only
   * `r !== prose`, so with prose undefined the body role classified as an
   * ordinary scalar and went into the compact properties row. Opening the one
   * record the role is about drew its prose as a property chip and offered no
   * reading surface at all.
   *
   * Suppression still outranks it, as it does for every list on this page.
   */
  /* AND THE DECLARED ROLES THE SHAPE NO LONGER HOLDS — @Codex, P2 on
     `57138aa`, and it is the paragraph above happening one source further out.
     That one is about the coverage FLOOR dropping a sparse role; this is about
     the role leaving `req.shape.roles` altogether when its last value is
     cleared, while `req.declaredRoles` keeps it.
     
     The consequence is the same two-step fall the comment above describes, and
     the declared-scalar repair below made it reachable: `prose` stayed
     undefined, so no trailing body field was emitted — and `isScalar` excludes
     only `r !== prose`, so with prose undefined the body classified as an
     ordinary scalar and the repair put it in the compact properties row. An
     emptied note's document surface became a property chip. */
  const declared = [...req.shape.roles.map((r) => r.role), ...(req.declaredRoles ?? [])]
    .filter((r) => !suppressed(r));
  const prose = ranked.find((r) => r === 'body') ?? ranked.find((r) => r === 'transcript')
    ?? declared.find((r) => r === 'body') ?? declared.find((r) => r === 'transcript');
  // From the SHAPE, ordered by coverage. Every relation role carries the same
  // rank weight, so this reproduces `rankRoles`' order for relations exactly
  // and skips only the floor.
  /**
   * RANKED FIRST, then the declared relations the floor dropped — @Codex's
   * P2 on 49cc0e3, and it is a regression I introduced.
   *
   * `taste.prominentRoles` can promote a lower-coverage relation above
   * another, and `ranked` already carries that order. Rebuilding the list
   * from the raw shape and sorting by coverage threw the person's Promote
   * away — the fix for the coverage floor reaching one dimension too far.
   *
   * So: `ranked`'s relations keep their order, and only the DECLARED
   * relations the floor removed are appended below them. That is the whole
   * of what reading past `ranked` was for.
   */
  const relations = (() => {
    const fromShape = [
      ...ranked.filter((r) => isRelationRole(r) && !suppressed(r)),
      ...[...req.shape.roles]
        .filter((r) => isRelationRole(r.role) && !suppressed(r.role)
          && !ranked.includes(r.role))
        .sort((a, b) => b.coverage - a.coverage)
        .map((r) => r.role),
    ];
    /**
     * AND A DECLARED RELATION WITH NO VALUES LEFT — @Codex's P2 on `4a34aef`,
     * and the FOURTH time one shape has cost a role its surface.
     *
     * `req.shape.roles` is what the pool HOLDS. Clear the last value of a
     * schema-declared relation and the role leaves it while staying in
     * `req.declaredRoles`, which `types.ts` keeps for exactly this — its own
     * comment says declared-but-empty counts as PRESENT. Both lists above
     * read the shape, so the regenerated object page dropped the relation's
     * only `node-slot` and left no surface from which to put a value back:
     * the one gesture that empties a relation is the one that makes it
     * unreachable.
     *
     * The same shape has now taken relations past the coverage floor
     * (`ed4f7a7`), attachments past it (`2e06944`), and prose (`7c286bf`) —
     * each read a list that answers "what is populated" while asking "what
     * does this object have". Appended last so ranked order and a Promote
     * still decide what comes first, with suppression outranking both.
     */
    const already = new Set(fromShape);
    return [...fromShape, ...(req.declaredRoles ?? [])
      .filter((r) => isRelationRole(r) && !suppressed(r) && !already.has(r))];
  })();
  /**
   * ATTACHMENTS COME FROM THE SHAPE TOO — @Codex's P2 on `ed4f7a7`, and it
   * is the relation fix above left half-applied.
   *
   * `ranked` drops every role under `roleCoverageFloor`, and that floor is a
   * PRESENTATION rule about a lens. Relations were taught to read past it
   * two rounds ago, for the reachability argument written above. An
   * attachment fails the same way and worse: one record in a large pool owns
   * the only image, so `media.hero` sits far below the floor, and opening
   * THAT RECORD — the one object the role is about — emits no media node.
   * The page whose whole subject is the photo is the page that cannot show
   * it.
   *
   * Same construction as the relations: ranked order first, so a Promote is
   * preserved, then the declared roles the floor removed, by coverage.
   * Suppression outranks both, as it does there.
   */
  /* AND A DERIVATIVE IS NOT A SECOND ATTACHMENT — @Codex's P2 on `2e06944`.
     Ingestion writes `media.thumb` beside its `media.hero` source on the same
     object, so both entered this list and the page drew the picture AND its
     low-resolution copy as separate content, one under the other. The generic
     single-object path already draws this line: `A SINGLE object keeps the
     source` in `baseline.ts` — the derivative exists so fifty cards do not
     each fetch one, and a page about the picture wants the picture.
     `DERIVATIVE_OF` is the declaration, so this is the same distinction and
     not a second list to keep in step.

     Only when the SOURCE is here. A poster frame whose video the shape does
     not carry stands for nothing else and is the attachment — the orphan case
     `0162` names, applied from the other side. */
  /* AND THE SHAPE CANNOT ANSWER IT — @Codex's P2 on `4a34aef`, withdrawing
     the exclusion the comment above describes and moving it, not dropping it.

     This asked the AGGREGATE shape whether the derivative's source appears
     anywhere in the pool, and excluded the derivative if it did. Over a
     heterogeneous pool the two can sit on DISJOINT records — the repository's
     own fixture shows that role presence and equal coverage prove nothing
     about overlap — so a thumb-only record was left with the hero node, which
     has no value there, and its only image vanished.

     Redundancy is a fact about ONE OBJECT and the generator never sees one.
     Both roles are emitted now, and `render-html.ts`'s `media()` stands the
     derivative down for its source per object: a record with both draws the
     picture, a record with only the thumbnail draws the thumbnail. The
     `2e06944` claim is unchanged and the orphan case `0162` names falls out
     of it for free, because a source with no value cannot suppress anything. */
  const isAttachment = (r: Role) => kinds(r) === 'media' || kinds(r) === 'av' || kinds(r) === 'doc';
  const attachments = [
    ...ranked.filter((r) => isAttachment(r) && !suppressed(r)),
    ...[...req.shape.roles]
      .filter((r) => isAttachment(r.role) && !suppressed(r.role) && !ranked.includes(r.role))
      .sort((a, b) => b.coverage - a.coverage)
      .map((r) => r.role),
  ];
  /**
   * PROVENANCE IS A PROPERTY — @Codex's P2 on 49cc0e3, and the other half of
   * the relation fix. Excluding `source` from Connections was right: it is
   * where a value came from, not a link a person may add, and
   * `mutationLegalFor` refuses `link` on it. But the property filter dropped
   * every ref-kind role too, so `source` then appeared NOWHERE — while the
   * generic single-object builder keeps it as a read-only reference field.
   * Removing a role from one place is not the same as removing it from the
   * page.
   */
  /**
   * AND SCALARS COME FROM THE SHAPE TOO — @Codex's P2 on `daafee9`, and it is
   * the relation and attachment fixes above left a third short.
   *
   * `ranked` drops every role under `roleCoverageFloor`, which is a
   * PRESENTATION rule about a lens over MANY objects. Relations were taught to
   * read past it, then attachments were, for the same reachability argument
   * both comments make. An ordinary scalar fails identically: `status` or
   * `date` filled on a handful of records in a large pool sits below the
   * floor, and opening one of THOSE records — the ones the role is about —
   * emitted no field for it. The value is stored, and the page that is
   * entirely about that object neither shows it nor offers anywhere to
   * change it.
   *
   * Same construction as its two neighbours: ranked order first so a Promote
   * is preserved, then the declared roles the floor removed, by coverage.
   * Suppression outranks both, as it does there.
   */
  const isScalar = (r: Role) => r !== 'title' && r !== prose && !isRelationRole(r)
    && kinds(r) !== 'media' && kinds(r) !== 'av' && kinds(r) !== 'doc';
  const fromShape = [
    ...ranked.filter(isScalar),
    ...[...req.shape.roles]
      .filter((r) => isScalar(r.role) && !suppressed(r.role) && !ranked.includes(r.role))
      .sort((a, b) => b.coverage - a.coverage)
      .map((r) => r.role),
  ];
  /**
   * AND A DECLARED SCALAR WITH NO VALUES LEFT — @Codex's P2 on `026c328`, and
   * the FIFTH time one shape has cost a role its surface.
   *
   * The comment on the relations list above says this in full and the
   * mechanism is identical: `req.shape.roles` is what the pool HOLDS, so
   * clearing the last value of a schema-declared scalar takes the role out of
   * it while `req.declaredRoles` keeps it — `types.ts` keeps that list for
   * exactly this, and its own comment says declared-but-empty counts as
   * PRESENT. Both halves above read the shape, so the regenerated object page
   * dropped the field and left nowhere to put a value back. The one gesture
   * that empties a property is the one that makes it unreachable.
   *
   * The tally is the point: relations past the coverage floor (`ed4f7a7`),
   * attachments past it (`2e06944`), prose (`7c286bf`), declared-but-empty
   * relations (`4a34aef`), and now scalars. Each read a list that answers
   * *what is populated* while asking *what does this object have*, and each
   * was found by a partner rather than by the fix before it generalising.
   *
   * Appended last, so ranked order and a Promote still decide what comes
   * first, with suppression outranking both — the same construction its four
   * predecessors use.
   *
   * WHAT THIS DOES NOT RESTORE, and saying so is part of it: an ENUM. @Codex,
   * P2 on `57138aa`. The field comes back, and `bindingFor` mints it
   * `direction: 'read'` because `chooseableEnums` reads `shape.roles` and
   * `shape.enumCardinality`, both of which the role left when its last value
   * was cleared — so the host derives no choices either. A text scalar can be
   * typed back; a cleared `status` gets a surface it cannot act through.
   *
   * Not fixed here because the options have nowhere to come from: the
   * declaration data would have to reach `chooseableEnums`, which takes a
   * shape rather than a request. Filed as `0202` with the shape of it, rather
   * than left as a promise this comment would otherwise be making.
   */
  const alreadyAProperty = new Set(fromShape);
  const properties = [
    ...fromShape,
    ...(req.declaredRoles ?? [])
      .filter((r) => isScalar(r) && !suppressed(r) && !alreadyAProperty.has(r)),
  ];
  return [
    // WRITABLE, which is the whole point of 0176: `bind` rather than `read`.
    // A collection's card stays `read` so it remains one hit target.
    { kind: 'field', id: `${prefix}-obj-title`, binding: bind(`${prefix}-obj-title`, 'title') } as TNode,
    // DIRECT CHILDREN, not a region of their own: `purpose` has no `media`
    // member and inventing one would be a vocabulary change made to hold a
    // layout decision. The nodes stand where the runtime already draws them.
    // AN ASPECT, or a still renders at zero height — @Codex's P2 on
    // 49cc0e3. A still is a background-image div with no intrinsic size, and
    // the runtime's only zero-collapse repair is scoped to
    // `.m-region.m-row > .m-media`; an object page is a STACK, so the node
    // existed and drew nothing. The attachment I restored was invisible.
    // A player (av) carries its own box and needs none.
    ...attachments.map((r, i) => ({
      kind: 'media', id: `${prefix}-obj-media-${i}`,
      binding: { slot: `${prefix}-obj-media-${i}`, role: r, direction: 'read' },
      fit: 'contain', optional: true,
      ...(kinds(r) === 'media' ? { aspect: '16/9' } : {}),
    }) as unknown as TNode),
    ...(properties.length
      ? [{
        kind: 'region', id: `${prefix}-obj-props`, purpose: 'properties', flow: 'row',
        children: properties.map((r, i) => ({
          kind: 'field', id: `${prefix}-obj-prop-${i}`, binding: bind(`${prefix}-obj-prop-${i}`, r),
        })),
      } as unknown as TNode]
      : []),
    ...(relations.length
      ? [{
        kind: 'region', id: `${prefix}-obj-links`, purpose: 'relations', flow: 'flow',
        children: relations.map((via) => ({
          kind: 'node-slot', id: `${prefix}-obj-rel-${via.replace(/\./g, '-')}`, via, maxDepth: 1,
        })),
      } as unknown as TNode]
      : []),
    ...(prose ? [{ kind: 'field', id: `${prefix}-obj-body`, binding: bind(`${prefix}-obj-body`, prose) } as TNode] : []),
  ];
}

function fibery(req: GenerationRequest, ranked: readonly Role[], bind: Bind): TLayoutSpec {
  const kinds = (r: Role) => ROLE_KIND[r];
  const roles = req.shape.roles.map((r) => r.role);
  const state = ranked.find((r) => kinds(r) === 'enum')
    ?? roles.find((r) => kinds(r) === 'enum')!;
  // Two views today: the board and the table. A kept calendar or timeline
  // view of another look opens here as the board.
  const view: TemplateView = req.templateView === 'table' ? 'table' : 'board';
  const read = (slot: string, role: Role): TBinding =>
    ({ slot, role, direction: 'read' }) as TBinding;
  const mod = (id: string, capability: 'workspace-nav' | 'workspace-topbar' | 'workspace-pagehead' | 'view-switcher' | 'workspace-search',
    channels?: Record<string, TBinding>): TNode => ({
    kind: 'capability', id, capability, version: CAPABILITIES[capability].version,
    ...(channels ? { channels } : {}),
  }) as unknown as TNode;
  const drawable = (r: Role) => kinds(r) !== 'media' && kinds(r) !== 'av' && kinds(r) !== 'doc';
  // A card's CHIPS: the fields a Fibery card carries under its title —
  // the ranked properties that are not the column's own state and are not
  // relations (see the chips expression below for why). A relation inside
  // the card's hit target draws as a span the host still routes, so the
  // foot chip opens what it points at and the card opens the card — the
  // runtime's rule, not this file's.
  const fields = ranked.filter((r) => r !== 'title' && r !== state && drawable(r));
  // Four at most, on the chips below: the reference's cards carry two or
  // three tags. The cap is a bound, not the shape of any card measured —
  // the tasks pool binds two.
  // SWIMLANES: the reference's board is a matrix — lanes (Engineering,
  // Product) across the state columns. The lane is a real second grouping
  // the shape carries or nothing: the parent relation (a feature's sprint)
  // when the shape has one. Drawn as rows by `flow: 'grid'` with the
  // `subGroupBy`. Its role is ALSO the card's foot band — see below.
  const lane = roles.find((r) => r === 'relation.parent');
  // NO RELATION IS A CHIP. The chips are this card's non-relation
  // properties; the ONE relation a Fibery board card draws is the foot,
  // below. Measured, task 0180: the reference's board is five columns of
  // one card each in that capture, and no card shows a person anywhere —
  // the chips on them are category tags (API/Backend, Mobile/Analytics,
  // Frontend/Web), and the only relation any of them draws is the
  // bordered parent row at the foot. Ours bound the assignee as a third
  // chip, which overflowed a 166px content width by one pixel and put it
  // on a row of its own: 26 css of card height plus its gap, on a card
  // measuring 200 against the reference's 129.
  //
  // Written as a KIND rule and not as a list of roles. Withholding
  // relation.peer by name would be a constant where a rule belongs, and
  // it would say nothing about relation.child or any role added later;
  // this says what the reference actually does, which is that a board
  // card's chip row is its properties and its relational context is one
  // band.
  const chips = fields.filter((r) => kinds(r) !== 'ref').slice(0, 4);
  // AND THE LANE'S ROLE COMES BACK, as the card's FOOT — the fourth
  // information band the reference's cards carry and ours did not. The
  // comment above used to end "the lane's own chip leaves the card, the
  // lane names it", and @Codex overruled that on 2026-09-03 after their
  // own two answers had collided and they resolved them:
  //
  //   "the swimlane communicates spatial grouping, the footer communicates
  //    card-local relational context … rendering both is truthful
  //    duplication of a role, not duplication of data"
  //
  // and the omission "optimizes away a visible reference component". They
  // also ruled out the alternative — seeding a Goal or an Epic to fill the
  // band — because a new relation is warranted only when the domain calls
  // for one, not as presentation scaffolding.
  //
  // It is emitted LAST and under its own slot name, so the renderer can
  // give it the foot band's presentation without guessing from position.
  const foot = lane !== undefined && fields.includes(lane) ? [lane] : [];
  const board: TNode = {
    kind: 'collection', id: 'fb-board', flow: lane ? 'grid' : 'stack',
    // `create`: "+ New" at the foot of each column, an entity in that
    // state — the host's create door.
    affordances: ['select', 'navigate', 'create'],
    query: { groupBy: state, ...(lane ? { subGroupBy: lane } : {}) },
    items: [{
      spec: {
        kind: 'card', id: 'fb-card', affordances: ['select'],
        // READ, deliberately, where the table's cells are written — task
        // 0176, and the reason is measured rather than argued. A card is a
        // BUTTON that opens its page: the whole card is `.m-card-hit`. Give
        // its title a write grant and the renderer stops emitting that hit —
        // an editable inside a button is not a thing a browser or a screen
        // reader can make sense of — so the board becomes unopenable. Driven:
        // `notion-look` timed out waiting for `.m-card-hit` and `fibery-look`
        // went 25/29 with the badge, the padding and the per-value chips
        // gone with it. Renaming in place on a board needs an affordance
        // that is not the whole card; that half of 0176 is still open.
        bindings: [read('fb-card-name', 'title'), ...chips.map((r) => read(`fb-card-${r}`, r)),
          ...foot.map((r) => read('fb-card-foot', r))],
      },
    }],
  } as unknown as TNode;
  // The table: the name, then every ranked property — the relations too,
  // as chips in their cells (an assignee who is an entity is the point of
  // a Fibery field).
  const table: TNode = {
    kind: 'collection', id: 'fb-table', flow: 'stack',
    affordances: ['select', 'navigate'],
    items: [{
      spec: {
        kind: 'card', id: 'fb-row', affordances: ['select'],
        bindings: [
          bind('fb-row-name', 'title'),
          ...ranked.filter((r) => r !== 'title' && drawable(r)).slice(0, 6).map((r) => bind(`fb-row-${r}`, r)),
        ],
      },
    }],
  } as unknown as TNode;
  /* ── the view over ONE OBJECT ──────────────────────────────────────────
   *
   * Task 0176, the same ruling as Notion's and for the same measurement:
   * Fibery's board opened a card and produced the same card, alone, still a
   * button, its title read-only — 0 editable fields on the board and 0 on the
   * page a card opens. Built from the three components the Obsidian editor
   * pane already uses, so it costs no new component here either.
   */
  const objectPage: TNode[] = objectPageNodes('fb', req, ranked, bind);
  const one = req.shape.cardinality === 'single';
  const page: TNode = {
    kind: 'region', id: 'fb-view', purpose: 'editor', flow: 'stack',
    children: one
      ? [mod('fb-viewbar', 'workspace-pagehead', { label: read('fb-view-name', 'title') }), ...objectPage]
      : [
        mod('fb-viewbar', 'workspace-pagehead', { label: read('fb-view-name', 'title') }),
        mod('fb-views', 'view-switcher'),
        mod('fb-search', 'workspace-search'),
        view === 'table' ? table : board,
      ],
  } as unknown as TNode;
  const main: TNode = {
    kind: 'region', id: 'fb-main', flow: 'stack',
    children: [mod('fb-topbar', 'workspace-topbar', { label: read('fb-tab-name', 'title') }), page],
  } as unknown as TNode;
  const root = {
    kind: 'region', id: 'fb-workspace', flow: 'row',
    children: [mod('fb-nav', 'workspace-nav'), main],
  } as unknown as TNode;
  return {
    specVersion: SPEC_VERSION, vocabularyVersion: VOCABULARY_VERSION,
    template: { id: 'app.fibery', version: APP_TEMPLATE_VERSIONS['app.fibery'] },
    // PRESERVED, for the reason Notion's is: the shape above is gated on this
    // value, so coercing `single` to `small-set` here would gate on a constant
    // — @Codex, *"destroys the discriminator the validator needs"*.
    target: {
      cardinality: req.shape.cardinality,
      // FROM THE EMITTED TREE, never the declaration — `emitsMediaNode` carries
      // the whole argument. Surface-scoped taste is keyed by (context,
      // cardinality, mediaWeight), so a page that draws no still and no
      // recording must not learn in the mixed-media cell.
      //
      // Two earlier spellings, kept because the direction of both errors is
      // the point: `req.shape.hasMedia` alone (@Codex on 49cc0e3) stamped the
      // BOARD and the TABLE `mixed`, and `hasMedia && cardinality === single`
      // (their P2 on `ee1dd16`) still stamped an object page `mixed` when the
      // person had HIDDEN its image. Each repair added a condition; neither
      // asked whether a media node was emitted.
      mediaWeight: emitsMediaNode(root) ? 'mixed' : 'text',
      context: 'browsing',
    },
    density: 'dense', typography: 'utility', palette: 'neutral',
    root,
  } as unknown as TLayoutSpec;
}

/**
 * `app.word` — a document with the ribbon (decision 0055's fourth look, task
 * 0170). Read from the pinned reference (`canon-looks.md`, the Wikipedia
 * Word screenshot): a title bar naming the document, the ribbon — a tab row
 * and a band of grouped controls with their labels beneath — the white
 * sheet on a pale desk, and a status bar with the word count. The sheet is
 * the `document-flow` module the host's own document view uses, paginated,
 * with its contents, one section per object; the ribbon draws only what
 * the host backs.
 */
function word(req: GenerationRequest, bind: Bind): TLayoutSpec {
  const kinds = (r: Role) => ROLE_KIND[r];
  const roles = req.shape.roles.map((r) => r.role);
  const prose = roles.find((r) => r === 'body') ?? roles.find((r) => r === 'transcript')
    ?? roles.find((r) => kinds(r) === 'text' && r !== 'title');
  const author = roles.find((r) => r === 'author') ?? roles.find((r) => kinds(r) === 'date');
  const read = (slot: string, role: Role): TBinding =>
    ({ slot, role, direction: 'read' }) as TBinding;
  const mod = (id: string, capability: 'workspace-topbar' | 'doc-ribbon' | 'workspace-status',
    channels?: Record<string, TBinding>): TNode => ({
    kind: 'capability', id, capability, version: CAPABILITIES[capability].version,
    ...(channels ? { channels } : {}),
  }) as unknown as TNode;
  const field = (id: string, role: Role) => ({ kind: 'field', id, binding: read(id, role) });
  // One section per object, named by its title, its prose writable where the
  // vocabulary can say how — the host's own document view's card.
  const section = {
    kind: 'card', id: 'section', affordances: ['expand'],
    bindings: [bind('sec-t', 'title'), ...(prose ? [bind('sec-b', prose)] : [])],
  };
  const sheet: TNode = {
    kind: 'capability', id: 'wd-doc', capability: 'document-flow', version: CAPABILITIES['document-flow'].version,
    config: { columns: 1, paginated: true, toc: true, footnotes: roles.some((r) => kinds(r) === 'ref') },
    ...(prose ? { channels: { body: read('doc-body', prose) } } : {}),
    slots: {
      body: [{ kind: 'collection', id: 'sections', flow: 'stack', items: [{ spec: section }] }],
      header: [field('doc-head', 'title')],
      ...(author ? { footer: [field('doc-foot', author)] } : {}),
    },
  } as unknown as TNode;
  return {
    specVersion: SPEC_VERSION, vocabularyVersion: VOCABULARY_VERSION,
    template: { id: 'app.word', version: APP_TEMPLATE_VERSIONS['app.word'] },
    // A SMALL SET whatever the pool's size — the document view's own target:
    // the sections flow down one sheet, and a large-set target would make a
    // grid of cards of them.
    target: { cardinality: 'small-set', mediaWeight: 'text', context: 'authoring' },
    density: 'balanced', typography: 'utility', palette: 'neutral',
    root: {
      kind: 'region', id: 'wd-window', flow: 'stack',
      children: [
        mod('wd-titlebar', 'workspace-topbar', { label: read('wd-doc-name', 'title') }),
        mod('wd-ribbon', 'doc-ribbon'),
        { kind: 'region', id: 'wd-desk', purpose: 'editor', flow: 'stack', children: [sheet] },
        mod('wd-status', 'workspace-status', prose ? { words: read('wd-words', prose) } : undefined),
      ],
    },
  } as unknown as TLayoutSpec;
}

/**
 * `app.coda` — a doc with a table (decision 0055's third look, task 0170).
 * Read from the pinned reference (`canon-looks.md`, the "Project Kickoff /
 * Roadmap" screenshot): the page-tree sidebar, the topbar, a page with a
 * cover, breadcrumb, icon and title and a paragraph, BUTTONS as blocks,
 * then the table — avatar and pill cells, hairline rows. The page head, the
 * buttons and the sidebar are registered modules the host feeds; the table
 * is the runtime's own presentation wearing the template's tokens: the
 * title and the ranked properties as columns, `create` for the button.
 */
function coda(req: GenerationRequest, ranked: readonly Role[], bind: Bind): TLayoutSpec {
  const kinds = (r: Role) => ROLE_KIND[r];
  const read = (slot: string, role: Role): TBinding =>
    ({ slot, role, direction: 'read' }) as TBinding;
  const mod = (id: string, capability: 'workspace-nav' | 'workspace-topbar' | 'workspace-pagehead' | 'doc-actions',
    channels?: Record<string, TBinding>): TNode => ({
    kind: 'capability', id, capability, version: CAPABILITIES[capability].version,
    ...(channels ? { channels } : {}),
  }) as unknown as TNode;
  const table: TNode = {
    kind: 'collection', id: 'cd-table', flow: 'stack',
    affordances: ['select', 'navigate', 'create'],
    items: [{
      spec: {
        kind: 'card', id: 'cd-row', affordances: ['select'],
        // `bind`, not `read` — task 0176. Coda is a table you TYPE IN, and
        // every one of its 84 drawn cells took no keystroke because these
        // said `read`. That was not a policy; it was the default nobody
        // chose, which is the same sentence `bindingFor`'s own docstring
        // records about the document view one layer down. `bind` degrades to
        // `read` for a kind with no mutation (a relation, a media), so the
        // cells that cannot be written still are not.
        bindings: [
          bind('cd-row-name', 'title'),
          ...ranked.filter((r) => r !== 'title' && kinds(r) !== 'ref' && kinds(r) !== 'media' && kinds(r) !== 'av' && kinds(r) !== 'doc')
            .slice(0, 6).map((r) => bind(`cd-row-${r}`, r)),
        ],
      },
    }],
  } as unknown as TNode;
  /* ── the doc over ONE ROW ───────────────────────────────────────────────
   *
   * Task 0176's third and last: Notion's page and Fibery's view had the same
   * defect and Coda has it in its own vocabulary. A Coda row EXPANDS — the
   * gesture opens that row's detail, its properties down a page — and what
   * this builder produced instead was the whole table again holding one row,
   * with the buttons still above it offering to create a second.
   *
   * Built from the same three component KINDS the other two use — `field`,
   * `region`, `node-slot` — so this costs no new component: a title field,
   * a properties region, a relations region of node-slots, a body field.
   * `bind` throughout, for the reason the row's own cells say — a detail you
   * cannot type in is the defect one layer down.
   *
   * What it DOES cost, said out loud: the property rows this task already
   * records as defective (`StatusOpen` run together, `Owner` wrapping) now
   * appear in a fourth look. That is a reason to fix them, not a reason to
   * keep Coda drawing a one-row table.
   */
  const rowPage: TNode[] = objectPageNodes('cd', req, ranked, bind);
  const one = req.shape.cardinality === 'single';
  const doc: TNode = {
    kind: 'region', id: 'cd-doc', purpose: 'editor', flow: 'stack',
    children: one
      ? [mod('cd-pagehead', 'workspace-pagehead', { label: read('cd-page-name', 'title') }), ...rowPage]
      : [
        mod('cd-pagehead', 'workspace-pagehead', { label: read('cd-page-name', 'title') }),
        mod('cd-actions', 'doc-actions'),
        table,
      ],
  } as unknown as TNode;
  const main: TNode = {
    kind: 'region', id: 'cd-main', flow: 'stack',
    children: [mod('cd-topbar', 'workspace-topbar', { label: read('cd-tab-name', 'title') }), doc],
  } as unknown as TNode;
  const root = {
    kind: 'region', id: 'cd-workspace', flow: 'row',
    children: [mod('cd-nav', 'workspace-nav'), main],
  } as unknown as TNode;
  return {
    specVersion: SPEC_VERSION, vocabularyVersion: VOCABULARY_VERSION,
    template: { id: 'app.coda', version: APP_TEMPLATE_VERSIONS['app.coda'] },
    // PRESERVED, the last of the three. This read `single ? 'small-set' : …`,
    // and the comment it never had would have said "a doc is a table, so a
    // single opens as a small one" — true while a single request emitted the
    // same table either way, and false the moment the shape above gates on
    // this value. @Codex on the first two: the coercion *"destroys the
    // discriminator the validator needs"*, so the gate would read a constant
    // and a single-object request could satisfy the template with a table.
    target: {
      cardinality: req.shape.cardinality,
      // FROM THE EMITTED TREE, never the declaration — `emitsMediaNode` carries
      // the whole argument. Surface-scoped taste is keyed by (context,
      // cardinality, mediaWeight), so a page that draws no still and no
      // recording must not learn in the mixed-media cell.
      //
      // Two earlier spellings, kept because the direction of both errors is
      // the point: `req.shape.hasMedia` alone (@Codex on 49cc0e3) stamped the
      // BOARD and the TABLE `mixed`, and `hasMedia && cardinality === single`
      // (their P2 on `ee1dd16`) still stamped an object page `mixed` when the
      // person had HIDDEN its image. Each repair added a condition; neither
      // asked whether a media node was emitted.
      mediaWeight: emitsMediaNode(root) ? 'mixed' : 'text',
      context: 'browsing',
    },
    density: 'balanced', typography: 'utility', palette: 'neutral',
    root,
  } as unknown as TLayoutSpec;
}

/**
 * `app.notion` — a page with a board view (decision 0055's second look,
 * written from the pinned reference: the "Ramp HQ" page and its "Company
 * tasks" board).
 *
 *   nav sidebar | topbar / page head / view switcher / board
 *
 * The page is the collection: its head names the pool, the switcher lists
 * the views the host can show it as, and the board is the runtime's own
 * board presentation — `groupBy` the first enum the shape carries (the
 * status), one card per object naming it by its title — wearing the
 * template's tokens: tinted columns, pill headers, hairline cards. No
 * region here is anything but layout; every app-faithful component is a
 * registered module fed by the host (the nav's sections, the topbar's tab,
 * the page's name and icon, the views and which is showing).
 */
function notion(req: GenerationRequest, ranked: readonly Role[], bind: Bind): TLayoutSpec {
  const kinds = (r: Role) => ROLE_KIND[r];
  const roles = req.shape.roles.map((r) => r.role);
  const status = ranked.find((r) => kinds(r) === 'enum')
    ?? roles.find((r) => kinds(r) === 'enum')!;
  const view: TemplateView = req.templateView ?? 'board';
  const read = (slot: string, role: Role): TBinding =>
    ({ slot, role, direction: 'read' }) as TBinding;
  const mod = (id: string, capability: 'workspace-nav' | 'workspace-topbar' | 'workspace-pagehead' | 'view-switcher' | 'workspace-search',
    channels?: Record<string, TBinding>): TNode => ({
    kind: 'capability', id, capability, version: CAPABILITIES[capability].version,
    ...(channels ? { channels } : {}),
  }) as unknown as TNode;
  const board: TNode = {
    kind: 'collection', id: 'nt-board', flow: 'stack',
    // `create`: "+ New page" at the foot of each column, a page in that
    // status — the reference's own row, the host's create door.
    affordances: ['select', 'navigate', 'create'],
    query: { groupBy: status },
    items: [{
      spec: {
        kind: 'card', id: 'nt-card', affordances: ['select'],
        // READ, deliberately, where the table's cells are written — task
        // 0176, and the reason is measured rather than argued. A card is a
        // BUTTON that opens its page: the whole card is `.m-card-hit`. Give
        // its title a write grant and the renderer stops emitting that hit —
        // an editable inside a button is not a thing a browser or a screen
        // reader can make sense of — so the board becomes unopenable. Driven:
        // `notion-look` timed out waiting for `.m-card-hit` and `fibery-look`
        // went 25/29 with the badge, the padding and the per-value chips
        // gone with it. Renaming in place on a board needs an affordance
        // that is not the whole card; that half of 0176 is still open.
        bindings: [read('nt-card-name', 'title')],
      },
    }],
  } as unknown as TNode;
  /**
   * THE PAGE'S VIEWS — the fifteenth verdict's list: the reference's row
   * carries four named views, and each is the same page with a different
   * subject presentation. TABLE: every ranked property as a column (the
   * runtime's table, in the template's tokens). CALENDAR and TIMELINE: the
   * registered modules, bound as the host's own view builders bind them —
   * the first date role, the title as the label, start and end when the
   * shape has them, the status as the timeline's lane.
   */
  const dates = roles.filter((r) => kinds(r) === 'date');
  const dateRole = dates.find((r) => r === 'date') ?? dates[0];
  const table: TNode = {
    kind: 'collection', id: 'nt-table', flow: 'stack',
    affordances: ['select', 'navigate'],
    items: [{
      spec: {
        kind: 'card', id: 'nt-row', affordances: ['select'],
        bindings: [
          bind('nt-row-name', 'title'),
          ...ranked.filter((r) => r !== 'title' && kinds(r) !== 'ref' && kinds(r) !== 'media' && kinds(r) !== 'av' && kinds(r) !== 'doc')
            .slice(0, 5).map((r) => bind(`nt-row-${r}`, r)),
        ],
      },
    }],
  } as unknown as TNode;
  const calendar: TNode = {
    kind: 'capability', id: 'nt-calendar', capability: 'calendar', version: CAPABILITIES['calendar'].version,
    config: { granularity: 'month' },
    channels: { ...(dateRole ? { date: read('nt-cal-date', dateRole) } : {}), label: read('nt-cal-label', 'title') },
  } as unknown as TNode;
  const has = (r: string) => roles.includes(r as Role);
  const timeline: TNode = {
    kind: 'capability', id: 'nt-timeline', capability: 'timeline', version: CAPABILITIES['timeline'].version,
    config: { granularity: 'day' },
    channels: {
      ...(has('date.start') ? { start: read('nt-tl-start', 'date.start' as Role) } : {}),
      ...(has('date.end') ? { end: read('nt-tl-end', 'date.end' as Role) } : {}),
      ...(has('date') ? { date: read('nt-tl-date', 'date' as Role) } : {}),
      label: read('nt-tl-label', 'title'),
      lane: read('nt-tl-lane', status),
    },
  } as unknown as TNode;
  const subject = view === 'table' ? table : view === 'calendar' ? calendar : view === 'timeline' ? timeline : board;

  /* ── the page over ONE OBJECT ──────────────────────────────────────────
   *
   * Task 0176, ruled by @Codex: *"Use the existing `nt-page` region, with
   * cardinality-dependent children … Single cardinality: object page
   * head/title, properties, and body/content. The single-object title binding
   * is writable. The collection's `nt-card` is absent at single cardinality;
   * therefore no inert button-shaped remnant remains."*
   *
   * And the reason it had to be built at all: opening a board card set the
   * cardinality to `single` and produced THE SAME CARD, alone, still carrying
   * `.m-card-hit`, its title read-only. There was nowhere in the look a person
   * could rename an object — measured, 0 editable fields on the board and 0 on
   * the page a card opens.
   *
   * Not one new component. The title is a `field`, the properties a region
   * with `purpose: 'properties'`, the relations a region with
   * `purpose: 'relations'` — the same three the Obsidian editor pane is built
   * from, which is what 0173 asks of every arrangement: a seventh app needs
   * tokens and an arrangement, not new components.
   */
  const objectPage: TNode[] = objectPageNodes('nt', req, ranked, bind);

  const one = req.shape.cardinality === 'single';
  const page: TNode = {
    kind: 'region', id: 'nt-page', purpose: 'editor', flow: 'stack',
    children: one
      ? [mod('nt-pagehead', 'workspace-pagehead', { label: read('nt-page-name', 'title') }), ...objectPage]
      : [
        mod('nt-pagehead', 'workspace-pagehead', { label: read('nt-page-name', 'title') }),
        mod('nt-views', 'view-switcher'),
        // The view row's Search: the same registered module the vault's
        // Search pane uses; here the host filters the BOARD by the query.
        mod('nt-search', 'workspace-search'),
        subject,
      ],
  } as unknown as TNode;
  const main: TNode = {
    kind: 'region', id: 'nt-main', flow: 'stack',
    children: [mod('nt-topbar', 'workspace-topbar', { label: read('nt-tab-name', 'title') }), page],
  } as unknown as TNode;
  const root = {
    kind: 'region', id: 'nt-workspace', flow: 'row',
    children: [mod('nt-nav', 'workspace-nav'), main],
  } as unknown as TNode;
  return {
    specVersion: SPEC_VERSION, vocabularyVersion: VOCABULARY_VERSION,
    template: { id: 'app.notion', version: APP_TEMPLATE_VERSIONS['app.notion'] },
    // The request's own cardinality, PRESERVED. It used to read "a board is
    // a set, so a single opens as a small one" and coerce `single` to
    // `small-set` — which was true while a single request emitted a
    // one-card board, and is what 0176 found: opening a card produced the
    // same card, alone, still a button, with no editable title anywhere.
    //
    // @Codex's ruling: *"The current `single -> small-set` coercion also
    // destroys the discriminator the validator needs and should not remain
    // the authoritative target cardinality."* The shape above is gated on
    // this value, so coercing it here would gate on a constant.
    target: {
      cardinality: req.shape.cardinality,
      // FROM THE EMITTED TREE, never the declaration — `emitsMediaNode` carries
      // the whole argument. Surface-scoped taste is keyed by (context,
      // cardinality, mediaWeight), so a page that draws no still and no
      // recording must not learn in the mixed-media cell.
      //
      // Two earlier spellings, kept because the direction of both errors is
      // the point: `req.shape.hasMedia` alone (@Codex on 49cc0e3) stamped the
      // BOARD and the TABLE `mixed`, and `hasMedia && cardinality === single`
      // (their P2 on `ee1dd16`) still stamped an object page `mixed` when the
      // person had HIDDEN its image. Each repair added a condition; neither
      // asked whether a media node was emitted.
      mediaWeight: emitsMediaNode(root) ? 'mixed' : 'text',
      context: 'browsing',
    },
    density: 'balanced', typography: 'utility', palette: 'neutral',
    root,
  } as unknown as TLayoutSpec;
}

/**
 * `app.obsidian` — a note open in its vault.
 *
 *   ribbon | explorer (siblings, by title) | tabs / editor / status | backlinks
 *
 * The editor is the subject: title, then every other ranked non-media role as
 * a properties row, then the prose, then a Connections region of the shape's
 * relation roles (attach and detach — the vocabulary's own, so a person can
 * link notes from inside the note as in Obsidian). The backlinks pane is a
 * collection whose rows the HOST supplies under the region's id (`regionSets`):
 * what points AT this note is a traversal against the store, which a spec
 * names and never performs.
 */
function obsidian(
  req: GenerationRequest, ranked: readonly Role[], bind: Bind,
): TLayoutSpec {
  const kinds = (r: Role) => ROLE_KIND[r];
  const prose = ranked.find((r) => r === 'body')
    ?? ranked.find((r) => r === 'transcript');
  const relations = ranked.filter((r) => kinds(r) === 'ref');
  // Properties: what is left after the title, the prose, the relations and
  // the media (a vault of notes carries none, and one that does draws it in
  // the note body, not in the properties row). A stored word count is a
  // property like any other; the status bar counts the prose itself.
  const properties = ranked.filter((r) =>
    r !== 'title' && r !== prose && kinds(r) !== 'ref'
    && kinds(r) !== 'media' && kinds(r) !== 'av' && kinds(r) !== 'doc');

  const read = (slot: string, role: Role): TBinding =>
    ({ slot, role, direction: 'read' }) as TBinding;

  /**
   * THE FOLDERS — the ninth verdict's list: *"explorer hierarchy with nested
   * folders."* A vault's folders are a partition of its notes, and the
   * vocabulary already has one: `query.groupBy`. The explorer groups the
   * siblings by the first ranked property a person reads as a NAME — an
   * enum first (a status, an origin), else a text that is not the title,
   * the prose or an identifier (an author) — and the renderer draws each
   * group as a folder with a disclosure under the vault's own. No such
   * role, no folders: the list stays flat under the vault rather than
   * inventing a partition. The role is in the spec, so a person can change
   * which folders the vault has by editing one line.
   */
  const folder = properties.find((r) => kinds(r) === 'enum')
    ?? properties.find((r) => kinds(r) === 'text' && r !== 'identifier');
  /**
   * ONE LEVEL DEEPER — the twelfth verdict: *"one genuinely nested folder
   * … the pinned reference mixes root notes with folders and deeper
   * nesting."* Inside each folder, the notes group again by the first
   * DATE property (`subGroupBy`), which the renderer draws as a month
   * folder — the way a vault of dated notes is kept, a year-month under
   * each author. No date, no second level; and never without the first.
   */
  const subFolder = folder ? properties.find((r) => kinds(r) === 'date') : undefined;

  const explorer: TNode = {
    kind: 'region', id: 'ws-explorer', purpose: 'explorer', flow: 'stack',
    children: [{
      kind: 'collection', id: 'ws-files', flow: 'stack', source: 'siblings',
      affordances: ['select', 'navigate'],
      ...(folder ? { query: { groupBy: folder, ...(subFolder ? { subGroupBy: subFolder } : {}) } } : {}),
      items: [{
        spec: {
          kind: 'card', id: 'ws-file', affordances: ['select'],
          bindings: [read('ws-file-name', 'title')],
        },
      }],
    }],
  } as unknown as TNode;

  const editorChildren: TNode[] = [
    { kind: 'field', id: 'ws-title', binding: bind('ws-title', 'title') } as TNode,
    ...(properties.length
      ? [{
        // A PROPERTIES block: named values under the title, as Obsidian
        // draws them. The purpose is what makes the renderer write the
        // names — the first deployed verdict read "Claude    2026-08-14"
        // and asked whose those were.
        kind: 'region', id: 'ws-properties', purpose: 'properties', flow: 'row',
        children: properties.map((r, i) => ({
          kind: 'field', id: `ws-prop-${i}`, binding: bind(`ws-prop-${i}`, r),
        })),
      } as unknown as TNode]
      : []),
    // The RELATIONS are properties too — list-valued ones, links — and in
    // Obsidian the properties panel sits above the prose, whole. The sixth
    // deployed verdict read the relation rows under the body as "the
    // strongest remaining form-editor residue"; putting them beside the
    // other properties, before the writing surface, is what the app does.
    ...(relations.length
      ? [{
        kind: 'region', id: 'ws-links', purpose: 'relations', flow: 'flow',
        children: relations.map((via) => ({
          kind: 'node-slot', id: `ws-rel-${via.replace(/\./g, '-')}`, via, maxDepth: 1,
        })),
      } as unknown as TNode]
      : []),
    ...(prose
      ? [{ kind: 'field', id: 'ws-body', binding: bind('ws-body', prose) } as TNode]
      : []),
  ];

  // The relation the graphs draw — declared here, above the main column,
  // because the vault's Graph view and the sidebar's local graph both read it.
  const edge = relations.find((r) => r === 'relation.peer') ?? relations[0];

  const main: TNode = {
    kind: 'region', id: 'ws-main', flow: 'stack',
    children: [
      {
        kind: 'capability', id: 'ws-tabs', capability: 'workspace-tabs',
        version: CAPABILITIES['workspace-tabs'].version,
        channels: { label: read('ws-tab-label', 'title') },
      },
      { kind: 'region', id: 'ws-editor', purpose: 'editor', flow: 'stack', children: editorChildren },
      /**
       * THE GRAPH VIEW — the tenth verdict's list: *"a main-workspace Graph
       * view … a dense exploratory canvas whose topology — not labels or
       * pane chrome — is the dominant image."* The whole vault as one
       * `force-graph` in `vault` mode, beside the editor in the main
       * column; which of the two shows is host state (a Graph view tab).
       * Only for a shape WITH a relation role, as the local graph is.
       */
      ...(edge === undefined ? [] : [{
        kind: 'region', id: 'ws-graph-view', purpose: 'graph', flow: 'stack',
        children: [{
          kind: 'capability', id: 'ws-vault-graph', capability: 'force-graph',
          version: CAPABILITIES['force-graph'].version,
          config: { depth: 1, showLabels: false, vault: true },
          channels: {
            label: read('ws-vault-graph-label', 'title'),
            edge: read('ws-vault-graph-edge', edge),
          },
        }],
      } as unknown as TNode]),
    ],
  } as unknown as TNode;

  // The status bar spans the WHOLE workspace, under every pane — the first
  // deployed verdict measured it spanning the editor only (x=313–1140) and
  // named the reference's full-width vault footer. It is the root's last
  // child, and the template stylesheet gives it the grid's last row.
  const status: TNode = {
    kind: 'capability', id: 'ws-status', capability: 'workspace-status',
    version: CAPABILITIES['workspace-status'].version,
    channels: {
      label: read('ws-status-label', 'title'),
      // The TEXT the bar counts — the host resolves this channel and counts
      // what it names, so a spec that binds a different prose role changes
      // the number without any host change.
      ...(prose ? { words: read('ws-status-words', prose) } : {}),
    },
  } as unknown as TNode;

  const backlinks: TNode = {
    kind: 'region', id: 'ws-backlinks', purpose: 'backlinks', flow: 'stack',
    children: [{
      kind: 'collection', id: 'ws-backlinks-list', flow: 'stack',
      affordances: ['select', 'navigate'],
      items: [{
        spec: {
          kind: 'card', id: 'ws-backlink', affordances: ['select'],
          bindings: [
            read('ws-backlink-name', 'title'),
            ...(prose ? [read('ws-backlink-text', prose)] : []),
          ],
        },
      }],
    }],
  } as unknown as TNode;

  /**
   * THE LOCAL GRAPH — the open note and what it links to or is linked from,
   * drawn by the registered `force-graph` module in the pane's own box
   * (`config.pane`). The edge role is the host's usual choice for a graph
   * (`relation.peer`, else any ref role); the label is the title. Only for
   * a shape WITH a relation role: a graph over nothing is a refusal wearing
   * a tab, and the sidebar then holds backlinks alone.
   */
  const graph: TNode | undefined = edge === undefined ? undefined : {
    kind: 'region', id: 'ws-graph', purpose: 'graph', flow: 'stack',
    children: [{
      kind: 'capability', id: 'ws-local-graph', capability: 'force-graph',
      version: CAPABILITIES['force-graph'].version,
      config: { depth: 1, showLabels: true, pane: true },
      channels: {
        label: read('ws-graph-label', 'title'),
        edge: read('ws-graph-edge', edge),
      },
    }],
  } as unknown as TNode;

  // The right SIDEBAR: backlinks and the graph share one column, one showing
  // at a time behind a strip of pane tabs the renderer draws from the panes
  // it holds. Which one shows is host state the renderer is told.
  const sidebar: TNode = {
    kind: 'region', id: 'ws-sidebar', purpose: 'sidebar', flow: 'stack',
    children: [backlinks, ...(graph ? [graph] : [])],
  } as unknown as TNode;

  /**
   * THE SEARCH PANE — the eighth verdict's list: a real Search tool with a
   * surface behind it. The query is the host's; the results are a subject
   * collection the host answers under `ws-search-results` (the backlinks
   * mechanism), each a card of the title and the prose it matched in.
   */
  const search: TNode = {
    kind: 'region', id: 'ws-search', purpose: 'search', flow: 'stack',
    children: [{
      // The query box and its count: a registered module the host feeds
      // (`workspace-search`), as the tab strip and the status bar are.
      kind: 'capability', id: 'ws-search-box', capability: 'workspace-search',
      version: CAPABILITIES['workspace-search'].version,
    }, {
      kind: 'collection', id: 'ws-search-results', flow: 'stack',
      affordances: ['select', 'navigate'],
      items: [{
        spec: {
          kind: 'card', id: 'ws-search-hit', affordances: ['select'],
          bindings: [
            read('ws-search-name', 'title'),
            ...(prose ? [read('ws-search-text', prose)] : []),
          ],
        },
      }],
    }],
  } as unknown as TNode;

  // The LEFT sidebar: the explorer and the search pane share the column,
  // as Obsidian's do; the ribbon's Files and Search tools choose.
  const left: TNode = {
    kind: 'region', id: 'ws-left', purpose: 'sidebar', flow: 'stack',
    children: [explorer, search],
  } as unknown as TNode;

  const root: TNode = {
    kind: 'region', id: 'ws-workspace', flow: 'row',
    children: [
      /**
       * THE WINDOW'S CONTROLS COME FIRST, in a row the columns begin beneath.
       *
       * @Codex's ruling on `470dfd5`, after arguing both sides of it: the tab
       * strip is the wrong carrier (controls drawn there begin 314px in and
       * read as decoration on the active tab) and so is the 44px ribbon (a
       * correctly spaced group spans 50px and would overlay the boundary).
       * A row of its own is neither, and it does not wait on the ribbon
       * becoming horizontal.
       *
       * Emitted for the look whether or not the host has controls to put in
       * it: an empty row draws nothing and costs nothing, and a spec that
       * changed shape according to host state would be a spec that depends on
       * the host, which 0055 puts the other way round.
       */
      {
        kind: 'capability', id: 'ws-window', capability: 'workspace-window',
        version: CAPABILITIES['workspace-window'].version,
      },
      {
        kind: 'capability', id: 'ws-ribbon', capability: 'workspace-ribbon',
        version: CAPABILITIES['workspace-ribbon'].version,
      },
      left, main, sidebar, status,
    ],
  } as unknown as TNode;

  return {
    specVersion: SPEC_VERSION,
    vocabularyVersion: VOCABULARY_VERSION,
    template: { id: 'app.obsidian', version: APP_TEMPLATE_VERSIONS['app.obsidian'] },
    target: {
      cardinality: req.shape.cardinality,
      mediaWeight: 'text',
      context: req.context,
    },
    // Dense chrome around a spacious reading surface is the whole of
    // Obsidian's density story, and it is drawn by the regions, not by this
    // scalar; the scalar is the editor's. Sans throughout — the reference
    // shows one geometric sans and no serif anywhere.
    density: req.taste?.density ?? 'balanced',
    typography: 'utility',
    palette: req.taste?.palette ?? 'neutral',
    root,
  } as TLayoutSpec;
}
